import type { FormEvent, ReactNode, Ref } from 'react';
import { Box, Button, Drawer, IconButton, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { SubmitButton } from './SubmitButton';

export interface FormDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Drawer title (sentence case, translated copy). */
  title: string;
  /** Optional subtitle under the title. */
  subtitle?: ReactNode;
  /** Form body (fields). */
  children: ReactNode;
  /**
   * Submit handler. When provided, the body is wrapped in a `<form>` so the
   * footer submit button and Enter both submit — pass `handleSubmit(...)` here.
   */
  onSubmit?: () => void;
  /** Footer submit label (translated copy). Requires `onSubmit`. */
  submitLabel?: string;
  /** Footer cancel label (translated copy). */
  cancelLabel?: string;
  submitPending?: boolean;
  submitDisabled?: boolean;
  /** Custom footer; overrides the default cancel/submit buttons. */
  footer?: ReactNode;
  /** Drawer width. */
  width?: number | string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Right-side slide-over for longer forms (new request, line-detail edit). Fixed
 * header and footer with a scrollable body, on MUI `Drawer`.
 */
export function FormDrawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  onSubmit,
  submitLabel,
  cancelLabel,
  submitPending = false,
  submitDisabled = false,
  footer,
  width = 460,
  ref,
}: FormDrawerProps) {
  const hasDefaultFooter = !footer && (submitLabel || cancelLabel);

  const inner = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: '18px 22px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontWeight: 400 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          <Close />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: '20px 22px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {children}
      </Box>

      {(footer || hasDefaultFooter) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.25,
            p: '14px 22px',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'paperAlt',
          }}
        >
          {footer ?? (
            <>
              {cancelLabel && (
                <Button variant="outlined" color="inherit" type="button" onClick={onClose}>
                  {cancelLabel}
                </Button>
              )}
              {submitLabel && (
                <SubmitButton
                  type={onSubmit ? 'submit' : 'button'}
                  pending={submitPending}
                  disabled={submitDisabled}
                >
                  {submitLabel}
                </SubmitButton>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Drawer ref={ref} anchor="right" open={open} onClose={onClose}>
      <Box
        component={onSubmit ? 'form' : 'div'}
        // Suppress native constraint validation (as FormDialog does): MUI puts
        // `required` on the underlying input, and the browser's own check aborts
        // submission before RHF runs — which would swallow the localized zod
        // messages in favour of an untranslated browser bubble.
        noValidate={onSubmit ? true : undefined}
        onSubmit={
          onSubmit
            ? (e: FormEvent) => {
                e.preventDefault();
                onSubmit();
              }
            : undefined
        }
        sx={{
          width,
          maxWidth: '100vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {inner}
      </Box>
    </Drawer>
  );
}
